import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { useEsc } from 'hooks/useEsc';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { UI_DATE_FORMAT_LONG } from 'lib/constants';
import { dateToIsoString, formatDate, isoDateStringToDate } from 'lib/riskbase';
import { smartText } from 'lib/str';
import { theme } from 'lib/theme';
import { cloneDeep, isEqual } from 'lodash';
import React from 'react';
import { LayoutChangeEvent, View } from 'react-native';
import { useDispatch } from 'react-redux';
import {
  ALERT,
  doEngageSaveContact,
  NAVIGATE_MODAL,
  NAVIGATE_MODAL_BACK,
} from 'store/actions';
import { TextField } from 'components/Field/TextField';
import { TextAreaField } from 'components/Field/TextAreaField/TextAreaField';
import {
  ContactAddedVia,
  DataSource,
  EngageFlatContact,
  FlatContactType,
} from 'types/Engage';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { SeparatorHorizontal } from 'components/SeparatorHorizontal';
import { selectEngageFlat } from 'store/remote/engageSelectors';
import { useSelector } from 'hooks/useSelector';
import { getFormattedFlatName } from 'store/remote/engageUtils';
import { ButtonToggleGroup } from 'components/ButtonToggleGroup';
import { ICONS } from 'components/Icon';
import { EngageFlatModalCommunicationsPage } from './EngageFlatModalCommunicationsPage';
import { createId } from '@paralleldrive/cuid2';
import { isEmail, isPhoneNumber } from 'class-validator';

type HandleSetContactType = (
  arg:
    | {
        isOwner: boolean;
        isResident: boolean;
        type?: never;
      }
    | { isOwner?: never; isResident?: never; type: FlatContactType },
) => void;

type HandleChangeWithName = <N extends keyof EngageFlatContact>(
  name: N,
  value: EngageFlatContact[N],
  doForceUpdate?: boolean,
) => void;

function normalizeContact(contact: EngageFlatContact): EngageFlatContact {
  const normalizedContact = { ...contact };
  if (normalizedContact.isOwner || !normalizedContact.isResident) {
    normalizedContact.isUnder16 = false;
  }
  if (!normalizedContact.isResident) {
    normalizedContact.hasSupportNeeds = false;
  }
  if (!normalizedContact.hasSupportNeeds) {
    normalizedContact.hasCognitiveImpairment = false;
    normalizedContact.hasHearingImpairment = false;
    normalizedContact.hasMobilityLimitation = false;
    normalizedContact.hasSocialAnxiety = false;
    normalizedContact.hasEmergencyEvacuationStatement = false;
    normalizedContact.hasGivenSupportNeedsConsent = false;
  }
  if (normalizedContact.isUnder16) {
    normalizedContact.firstName = '';
    normalizedContact.lastName = '';
    normalizedContact.phone = '';
    normalizedContact.email = '';
  }

  // @todo actually set this when !isOwner && !isResident
  normalizedContact.contactType =
    normalizedContact.isOwner && normalizedContact.isResident
      ? FlatContactType.residentOwner
      : normalizedContact.isOwner
        ? FlatContactType.owner
        : normalizedContact.isResident
          ? FlatContactType.resident
          : normalizedContact.contactType;

  return normalizedContact;
}

export const EngageContactModal: React.FC<
  ModalScreenProps<ModalScreens.EngageContact>
> = ({ route, navigation }) => {
  const dispatch = useDispatch();
  const confirmUnsaved = useConfirmUnsaved();
  const forceUpdate = useForceUpdate();

  const { key } = route.params;

  const [activeTab, setActiveTab] = React.useState<'information' | 'messages'>(
    'information',
  );

  const {
    flat: { name: flatName, id: flatId },
    property: { id: propertyId },
  } = useSelector((state) => selectEngageFlat(state, key))!;

  const initValues = React.useRef<EngageFlatContact>(
    route.params.contact
      ? {
          id: route.params.contact.id,
          firstName: route.params.contact.firstName,
          lastName: route.params.contact.lastName,
          email: route.params.contact.email,
          phone: route.params.contact.phone,
          isOwner: route.params.contact.isOwner,
          isResident: route.params.contact.isResident,
          isUnder16: route.params.contact.isUnder16,
          isPrimary: route.params.contact.isPrimary,
          internalNotes: route.params.contact.internalNotes,
          comments: route.params.contact.comments,
          hasSupportNeeds: route.params.contact.hasSupportNeeds,
          hasSocialAnxiety: route.params.contact.hasSocialAnxiety,
          hasHearingImpairment: route.params.contact.hasHearingImpairment,
          hasMobilityLimitation: route.params.contact.hasMobilityLimitation,
          hasCognitiveImpairment: route.params.contact.hasCognitiveImpairment,
          hasEmergencyEvacuationStatement:
            route.params.contact.hasEmergencyEvacuationStatement ?? false,
          hasGivenSupportNeedsConsent:
            route.params.contact.hasGivenSupportNeedsConsent ?? false,
          contactType: route.params.contact.contactType,
          isConfirmed: route.params.contact.isConfirmed,
          confirmedDate: route.params.contact.confirmedDate,
          addedTimestamp: route.params.contact.addedTimestamp,
          addedVia: route.params.contact.addedVia,
          archivedTimestamp: route.params.contact.archivedTimestamp,
          archivedVia: route.params.contact.archivedVia,
        }
      : {
          id: createId(),
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          isOwner: true,
          isResident: false,
          isUnder16: false,
          isPrimary: false,
          internalNotes: '',
          comments: '',
          hasSupportNeeds: false,
          hasSocialAnxiety: false,
          hasHearingImpairment: false,
          hasMobilityLimitation: false,
          hasCognitiveImpairment: false,
          hasEmergencyEvacuationStatement: false,
          hasGivenSupportNeedsConsent: false,
          contactType: FlatContactType.owner,
          isConfirmed: false,
          confirmedDate: null,
          addedTimestamp: dateToIsoString(new Date()),
          addedVia: DataSource.app,
          archivedTimestamp: null,
          archivedVia: null,
        },
  );

  const changes = React.useRef(cloneDeep(initValues.current));
  const checkIsDirty = React.useCallback(
    () => !isEqual(initValues.current, changes.current),
    [],
  );
  const isDirty = React.useRef<boolean>(false);
  isDirty.current = checkIsDirty();

  const isNew = !route.params.contact;
  const isArchived = !!changes.current.archivedTimestamp;

  const handleSetContactType: HandleSetContactType = ({
    isOwner,
    isResident,
    type,
  }) => {
    if (type) {
      changes.current.isOwner = false;
      changes.current.isResident = false;
      changes.current.contactType = type;
    } else {
      changes.current.isOwner = isOwner;
      changes.current.isResident = isResident;
      changes.current.contactType =
        isOwner && isResident
          ? FlatContactType.residentOwner
          : isOwner
            ? FlatContactType.owner
            : FlatContactType.resident;
    }
    forceUpdate();
  };

  const handleChangeWithName: HandleChangeWithName = React.useCallback(
    (name, value, doForceUpdate) => {
      changes.current[name] = value;
      if (doForceUpdate || isDirty.current !== checkIsDirty()) {
        forceUpdate();
      }
    },
    [checkIsDirty, forceUpdate],
  );

  const handleSave = React.useCallback(() => {
    const normalizedContact = normalizeContact(changes.current);
    const errors = [];

    if (
      !normalizedContact.isUnder16 &&
      (normalizedContact.firstName.trim() === '' ||
        normalizedContact.lastName.trim() === '')
    ) {
      errors.push('*First Name* and *Last Name* are required');
    }

    if (normalizedContact.email && !isEmail(normalizedContact.email)) {
      errors.push('*Email* is not valid');
    }

    if (
      normalizedContact.phone &&
      !(
        // Allow GB numbers without international code
        // Allow international number with international code
        (
          isPhoneNumber(normalizedContact.phone, 'GB') ||
          isPhoneNumber(normalizedContact.phone)
        )
      )
    ) {
      errors.push('*Phone* is not valid');
    }

    if (errors.length) {
      dispatch(
        ALERT({
          title: 'Contact Cannot Be Saved',
          text: errors.join('\n'),
        }),
      );
      return;
    }

    dispatch(
      doEngageSaveContact({
        key,
        propertyId,
        flatId,
        isNew,
        contact: normalizedContact,
      }),
    );
  }, [dispatch, flatId, isNew, key, propertyId]);

  const handleClose = React.useCallback(() => {
    if (isDirty.current) {
      confirmUnsaved({
        onSave: () => handleSave,
        onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  }, [confirmUnsaved, handleSave, dispatch]);

  const isDirtyVar = isDirty.current;
  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirtyVar });
  }, [navigation, isDirtyVar]);
  useEsc(handleClose);

  const NavigationHeaderMemo = React.useCallback(
    (allowSave: boolean) => (
      <NavigationHeader
        heading={
          isNew ? 'New Contact' : isArchived ? 'Archived Contact' : 'Contact'
        }
        subheading={getFormattedFlatName(flatName)}
        cancelButtonText="Close"
        onPressCancelButton={handleClose}
        onPressSaveButton={
          isArchived ? undefined : allowSave ? handleSave : null
        }
        menuRight={
          !isNew && {
            iconButtonConfig: {
              icon: ICONS.EllipsisLight,
            },
            options: [
              {
                text: 'Send Messages',
                onPress: () =>
                  dispatch(
                    NAVIGATE_MODAL({
                      screen: ModalScreens.EngageSendCommunication,
                      params: { key, contactId: changes.current.id },
                    }),
                  ),
              },
              '-',
              {
                text: 'Archive Contact',
                destructive: true,
              },
            ],
          }
        }
      />
    ),
    [dispatch, flatName, handleClose, handleSave, isArchived, isNew, key],
  );

  const ButtonToggleGroupMemo = React.useCallback(
    (thisActiveTab: 'information' | 'messages') => (
      <ButtonToggleGroup
        key={thisActiveTab}
        rounded
        buttons={[
          { text: 'Information', key: 'information' },
          { text: 'Messages', key: 'messages' },
        ]}
        active={thisActiveTab}
        onActiveChange={(newActive) => {
          setTimeout(() => setActiveTab(newActive), 1);
          return false;
        }}
        containerStyle={{
          marginBottom: theme.paddingVerticalHalf,
          alignSelf: 'stretch',
          marginHorizontal: theme.paddingHorizontal,
        }}
        size="small"
      />
    ),
    [],
  );

  return (
    <>
      <View
        style={[
          activeTab === 'information' ? { flex: 1 } : { display: 'none' },
        ]}
      >
        <EngageContactInfoPage
          HeaderItems={
            <>
              {NavigationHeaderMemo(true)}
              {!isNew && ButtonToggleGroupMemo('information')}
            </>
          }
          handleSetContactType={handleSetContactType}
          handleChangeWithName={handleChangeWithName}
          changes={changes}
          isNew={isNew}
        ></EngageContactInfoPage>
      </View>
      {!isNew && (
        <View
          style={[activeTab === 'messages' ? { flex: 1 } : { display: 'none' }]}
        >
          <EngageFlatModalCommunicationsPage
            pageKey={key}
            contactId={changes.current.id}
            HeaderItems={
              <>
                {NavigationHeaderMemo(false)}
                {ButtonToggleGroupMemo('messages')}
              </>
            }
          />
        </View>
      )}
    </>
  );
};

const EngageContactInfoPage: React.FC<{
  HeaderItems: React.ReactNode;
  handleSetContactType: HandleSetContactType;
  handleChangeWithName: HandleChangeWithName;
  changes: React.RefObject<EngageFlatContact>;
  isNew: boolean;
}> = ({
  HeaderItems,
  handleSetContactType,
  handleChangeWithName,
  changes,
  isNew,
}) => {
  const dispatch = useDispatch();

  const isArchived = !!changes.current.archivedTimestamp;

  const [breakpoints, setBreakpoints] = React.useState<{
    helpRequiredHorizontal: boolean;
  }>({ helpRequiredHorizontal: false });

  const handleLayout = React.useCallback(
    ({
      nativeEvent: {
        layout: { width },
      },
    }: LayoutChangeEvent) => {
      setBreakpoints({
        helpRequiredHorizontal: width > 500,
      });
    },
    [],
  );

  const collectDetails =
    changes.current.isOwner ||
    (changes.current.isResident && !changes.current.isUnder16) ||
    (!changes.current.isOwner && !changes.current.isResident);

  return (
    <PageScrollView
      isInModal={true}
      header={HeaderItems}
      onLayout={handleLayout}
    >
      <Card label="Relationship to Flat">
        <ViewWithSeparators horizontal>
          <CardRow
            label="Owner"
            radioValue={changes.current.isOwner && !changes.current.isResident}
            onPress={() =>
              handleSetContactType({ isOwner: true, isResident: false })
            }
            style={{ flex: 1 }}
          />
          <CardRow
            label="Owner & Resident"
            radioValue={changes.current.isOwner && changes.current.isResident}
            onPress={() =>
              handleSetContactType({
                isOwner: true,
                isResident: true,
              })
            }
            style={{ flex: 1 }}
          />
        </ViewWithSeparators>
        <ViewWithSeparators horizontal>
          <CardRow
            label="Resident"
            radioValue={!changes.current.isOwner && changes.current.isResident}
            onPress={() =>
              handleSetContactType({ isOwner: false, isResident: true })
            }
            style={{ flex: 1 }}
          />
          <CardRow
            label="Letting Agent"
            style={{ flex: 1 }}
            radioValue={
              changes.current.contactType === FlatContactType.lettingAgent
            }
            onPress={() =>
              handleSetContactType({ type: FlatContactType.lettingAgent })
            }
            disabled
          />
        </ViewWithSeparators>
        <ViewWithSeparators horizontal>
          <CardRow
            label="Behalf of Someone Else"
            style={{ flex: 1 }}
            radioValue={
              changes.current.contactType === FlatContactType.onBehalfOf
            }
            onPress={() =>
              handleSetContactType({ type: FlatContactType.onBehalfOf })
            }
            disabled
          />
          <CardRow
            label="Other"
            style={{ flex: 1 }}
            radioValue={changes.current.contactType === FlatContactType.other}
            onPress={() =>
              handleSetContactType({ type: FlatContactType.other })
            }
            disabled
          />
        </ViewWithSeparators>
      </Card>

      {(changes.current.contactType === FlatContactType.onBehalfOf ||
        changes.current.contactType === FlatContactType.other ||
        changes.current.contactType === FlatContactType.lettingAgent) && (
        <Card
          label="Other Description"
          explanation={smartText(
            changes.current.contactType === FlatContactType.onBehalfOf
              ? 'Describe how this person knows the owners / residents – for example, they are a carer or a family member'
              : changes.current.contactType === FlatContactType.lettingAgent
                ? 'Provide details about the letting agent'
                : "Describe this person's relationship to the flat",
          )}
        >
          <TextAreaField
            onChangeWithName={handleChangeWithName}
            name="comments"
            value={changes.current.comments}
          />
        </Card>
      )}

      {changes.current.isResident && !changes.current.isOwner && (
        <Card
          label="Resident Age"
          labelLinkText="Why is this important?"
          onLabelLinkPress={() => {
            dispatch(
              ALERT({
                title: 'Resident Age',
                text: smartText(
                  'The Building Safety Act and Fire Safety (England) Regulations place additional responsibilities on the Responsible and Accountable People relating to residents aged 16 and over.\n\n' +
                    "For data protection reasons, we don't collect names or contact details for people under 16. We only ask whether they might need help evacuating.\n\n" +
                    'If a resident under 16 is also an owner, their details are collected. This is because owners have additional legal responsibilities under the Building Safety Act.',
                ),
              }),
            );
          }}
          explanation={smartText(
            'Name, email, and phone are only collected for residents that are 16 or over.',
          )}
        >
          <CardRow
            value="Aged 16 or Over"
            onPress={
              isArchived
                ? undefined
                : () =>
                    handleChangeWithName(
                      'isUnder16',
                      !changes.current.isUnder16,
                      true,
                    )
            }
            checkedValue={!changes.current.isUnder16}
            disabled={changes.current.isOwner}
          />
        </Card>
      )}

      {collectDetails && (
        <ViewWithSeparators
          horizontal
          separatorColor="transparent"
          separatorMargin={theme.paddingHorizontalHalf}
          marginHorizontal
        >
          <Card
            style={{ flex: 1 }}
            label="First Name"
            required
            marginHorizontal={false}
            marginBottomHalf
          >
            {isArchived ? (
              <CardRow value={changes.current.firstName} />
            ) : (
              <TextField
                value={changes.current.firstName}
                containerStyle={{ flex: 1 }}
                name="firstName"
                onChangeWithName={handleChangeWithName}
                disabled={!collectDetails}
              />
            )}
          </Card>
          <Card
            style={{ flex: 1 }}
            label="Last Name"
            required
            marginHorizontal={false}
            marginBottomHalf
          >
            {isArchived ? (
              <CardRow value={changes.current.lastName} />
            ) : (
              <TextField
                value={changes.current.lastName}
                containerStyle={{ flex: 1 }}
                name="lastName"
                onChangeWithName={handleChangeWithName}
                disabled={!collectDetails}
              />
            )}
          </Card>
        </ViewWithSeparators>
      )}

      {collectDetails && (
        <ViewWithSeparators
          horizontal
          separatorColor="transparent"
          separatorMargin={theme.paddingHorizontalHalf}
          marginHorizontal
        >
          <Card label="Email" style={{ flex: 1 }} marginHorizontal={false}>
            {isArchived ? (
              <CardRow value={changes.current.email} placeholder="No Email" />
            ) : (
              <TextField
                value={changes.current.email}
                containerStyle={{ flex: 1 }}
                placeholder="No Email"
                name="email"
                onChangeWithName={handleChangeWithName}
                disabled={!collectDetails}
              />
            )}
          </Card>
          <Card label="Phone" style={{ flex: 1 }} marginHorizontal={false}>
            {isArchived ? (
              <CardRow value={changes.current.phone} placeholder="No Phone" />
            ) : (
              <TextField
                value={changes.current.phone}
                containerStyle={{ flex: 1 }}
                placeholder="No Phone Number"
                name="phone"
                onChangeWithName={handleChangeWithName}
                disabled={!collectDetails}
              />
            )}
          </Card>
        </ViewWithSeparators>
      )}

      {changes.current.isResident && (
        <Card
          label="Help Required When Evacuating"
          labelLinkText="Why is this important?"
          onLabelLinkPress={() => {
            dispatch(
              ALERT({
                title: 'Help Required When Evacuating',
                text: smartText(
                  'Asking whether residents can evacuate without help in an emergency (such as a fire) helps indicate who may need assistance if the building has to be evacuated quickly.\n\n' +
                    'Collecting this information helps plan for the safety of everyone in the building, including putting in place appropriate measures or support for residents who may have mobility, health, or other needs.\n\n' +
                    "This is part of our responsibilities under the Building Safety Act to engage with residents and ensure their needs are considered in the building's safety planning.",
                ),
              }),
            );
          }}
          marginBottomQuarter
        >
          <CardRow
            value="Resident requires help evacuating in an emergency"
            checkedValue={changes.current.hasSupportNeeds}
            onPress={
              isArchived
                ? undefined
                : () =>
                    handleChangeWithName(
                      'hasSupportNeeds',
                      !changes.current.hasSupportNeeds,
                      true,
                    )
            }
          />
        </Card>
      )}
      {changes.current.isResident && changes.current.hasSupportNeeds && (
        <>
          <Card marginBottomQuarter>
            <ViewWithSeparators horizontal={breakpoints.helpRequiredHorizontal}>
              <CardRow
                label="Has mobility limitation"
                value="i.e. Needs help with stairs"
                valueNumberOfLines={1}
                labelAlignTop
                style={{ flex: 1 }}
                checkedValue={changes.current.hasMobilityLimitation}
                onPress={
                  isArchived
                    ? undefined
                    : () =>
                        handleChangeWithName(
                          'hasMobilityLimitation',
                          !changes.current.hasMobilityLimitation,
                          true,
                        )
                }
              />
              <CardRow
                label="Has cognitive impairment"
                value="i.e. Struggles to follow instructions"
                valueNumberOfLines={1}
                labelAlignTop
                style={{ flex: 1 }}
                checkedValue={changes.current.hasCognitiveImpairment}
                onPress={
                  isArchived
                    ? undefined
                    : () =>
                        handleChangeWithName(
                          'hasCognitiveImpairment',
                          !changes.current.hasCognitiveImpairment,
                          true,
                        )
                }
              />
            </ViewWithSeparators>
            <ViewWithSeparators horizontal={breakpoints.helpRequiredHorizontal}>
              <CardRow
                label="Has social anxiety"
                value="i.e. Finds crowded spaces stressful"
                valueNumberOfLines={1}
                labelAlignTop
                style={{ flex: 1 }}
                checkedValue={changes.current.hasSocialAnxiety}
                onPress={
                  isArchived
                    ? undefined
                    : () =>
                        handleChangeWithName(
                          'hasSocialAnxiety',
                          !changes.current.hasSocialAnxiety,
                          true,
                        )
                }
              />
              <CardRow
                label="Has hearing impairment"
                value="i.e. May not hear the alarm"
                valueNumberOfLines={1}
                labelAlignTop
                style={{ flex: 1 }}
                checkedValue={changes.current.hasHearingImpairment}
                onPress={
                  isArchived
                    ? undefined
                    : () =>
                        handleChangeWithName(
                          'hasHearingImpairment',
                          !changes.current.hasHearingImpairment,
                          true,
                        )
                }
              />
            </ViewWithSeparators>
          </Card>
          {/* <Card marginBottomQuarter>
            <ViewWithSeparators horizontal={breakpoints.helpRequiredHorizontal}>
              <CardRow
                label="Has emergency evacuation statement"
                value="i.e. Personal evacuation plan in place"
                valueNumberOfLines={1}
                labelAlignTop
                style={{ flex: 1 }}
                checkedValue={changes.current.hasEmergencyEvacuationStatement}
                onPress={
                  isArchived
                    ? undefined
                    : () =>
                        handleChangeWithName(
                          'hasEmergencyEvacuationStatement',
                          !changes.current.hasEmergencyEvacuationStatement,
                          true,
                        )
                }
              />
              <CardRow
                label="Has given consent"
                value="i.e. Agreed to share support needs info"
                valueNumberOfLines={1}
                labelAlignTop
                style={{ flex: 1 }}
                checkedValue={changes.current.hasGivenSupportNeedsConsent}
                onPress={
                  isArchived
                    ? undefined
                    : () =>
                        handleChangeWithName(
                          'hasGivenSupportNeedsConsent',
                          !changes.current.hasGivenSupportNeedsConsent,
                          true,
                        )
                }
              />
            </ViewWithSeparators>
          </Card> */}
        </>
      )}

      {changes.current.isResident && (
        <View
          style={{
            height: theme.paddingVerticalHalf + theme.paddingVerticalQuarter,
          }}
        />
      )}

      <Card
        label="Notes"
        explanation="Notes are for internal use only and are not visible to contacts"
      >
        {isArchived ? (
          <CardRow
            placeholder="No Internal Notes"
            value={changes.current.internalNotes}
          />
        ) : (
          <TextAreaField
            placeholder="Internal Notes"
            name="internalNotes"
            value={changes.current.internalNotes}
            onChangeWithName={handleChangeWithName}
          />
        )}
      </Card>

      <SeparatorHorizontal withMarginBottom />

      <Card
        explanation={
          !isNew
            ? 'Added ' +
              `${formatDate(isoDateStringToDate(changes.current.addedTimestamp), UI_DATE_FORMAT_LONG)} ` +
              `via ${ContactAddedVia[changes.current.addedVia].name}${
                changes.current.archivedTimestamp && changes.current.archivedVia
                  ? '\nArchived ' +
                    `${formatDate(isoDateStringToDate(changes.current.archivedTimestamp), UI_DATE_FORMAT_LONG)} ` +
                    `via ${ContactAddedVia[changes.current.archivedVia].name}`
                  : ''
              }`
            : ''
        }
      />
    </PageScrollView>
  );
};
